import { useTranslation } from 'react-i18next';
import { Box, Button, Popover, Stack, styled, type PopoverPosition, type PopoverProps } from '@mui/material';
import type { Merge } from 'type-fest';

import { useProductSelectionStoreActions } from '../../../product-selection.store';
import { AlphabetContainer } from './AlphabetContainer';
import { CYRILLIC_ALPHABET, LATIN_ALPHABET } from './constants';

export const StyledAlphabetIndexContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 1, 1),

  '> .alphabetBlocks': {
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.custom.grey}`,
  },

  '> .resetButton': {
    marginTop: theme.spacing(1),
  },
}));

const computePopoverPosition = (anchorEl: HTMLButtonElement): PopoverPosition => {
  const anchorDomRect = anchorEl.getBoundingClientRect();

  return {
    top: anchorDomRect.top + anchorDomRect.height + 4,
    left: anchorDomRect.left,
  };
};

type AlphabeticalIndexPopoverProps = Merge<
  PopoverProps,
  {
    anchorEl: HTMLButtonElement | null;
    onClose: () => void;
  }
>;

export const AlphabeticalIndexPopover = (props: AlphabeticalIndexPopoverProps) => {
  const { open, anchorEl, onClose, ...restOfProps } = props;
  const { clearAlphabeticalIndexSearchQuery } = useProductSelectionStoreActions();
  const { t } = useTranslation('product-selection-alphabetical-index');

  const handleReset = () => {
    clearAlphabeticalIndexSearchQuery();
    onClose();
  };

  const handleLetterClick = () => onClose();

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      anchorReference="anchorPosition"
      anchorPosition={anchorEl ? computePopoverPosition(anchorEl) : undefined}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      onClose={onClose}
      {...restOfProps}
    >
      <StyledAlphabetIndexContainer>
        <Stack direction="row" spacing={3} className="alphabetBlocks" data-testid="alphabetical-index-container">
          <AlphabetContainer
            onSelected={handleLetterClick}
            title={t('alphabet-title.cyrillic')}
            alphabet={CYRILLIC_ALPHABET}
          />
          <AlphabetContainer
            onSelected={handleLetterClick}
            title={t('alphabet-title.latin')}
            alphabet={LATIN_ALPHABET}
          />
          <AlphabetContainer onSelected={handleLetterClick} title={t('alphabet-title.numeration')} />
        </Stack>
        <Button variant="outlined" color="inherit" onClick={handleReset} className="resetButton">
          {t('actions.reset')}
        </Button>
      </StyledAlphabetIndexContainer>
    </Popover>
  );
};
